import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Page, Dashboard } from '../../templates';
import { Content } from '../../organisms';
import  CadDataFilter from './Filter';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { BackdropPanel } from '../../atoms';
import Menu from '../../../utils/menu';
import { LanguageButton } from '../../molecules';
import i18n from '../../../utils/i18n';
import { useTranslation } from 'react-i18next';
import { translateText } from '../../../utils/translateText';
import palette from '../../../utils/colorPalette';
import { styled } from 'styled-components';
import * as XLSX from 'xlsx-js-style';
import { useMsal } from '@azure/msal-react';
import { useUser } from '../../../contexts/UserContext';
import Unauthorized from '../../auth/Unauthorized';
import { useLocation } from 'react-router-dom';

function CadDataDXUpload({ onSubTabChange = null }) {
  const { t } = useTranslation();
  const language = i18n.language;
  const location = useLocation();
  const ismatrixBoardPage = location.pathname === '/interstellar/matrix-board';
  const { accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [snowflakeData, setSnowflakeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [lifeStyleList, setLifeStyleList] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [translatedColumns, setTranslatedColumns] = useState(columns(language));
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [originalTableData, setOriginalTableData] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentFilterSesn, setCurrentFilterSesn] = useState([]);
  const { user, permissions, hasPermission, loading: userLoading } = useUser();
  const fileInputRef = useRef(null);

  const loadData = useCallback(() => {
    setIsLoading(true);
    fetch('/api/cad-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ brd_cd: 'X' })
    })
      .then(response => response.json())
      .then(data => {
        setSnowflakeData(data);
        setFilterOptions({
          designer: createOptions(data, 'dsgnr_nm'),
          item: createOptions(data, 'item'),
          class1: createOptions(data, 'parent_prdt_kind_nm'),
          class2: createOptions(data, 'prdt_kind_nm'),
          sesn: createOptions(data, 'sesn'),
          sex: createOptions(data, 'sex'),
          fabInfo: createOptions(data, 'fab_info'),
          lifeStyle: createOptions(data, 'lifestyle_nm'),
        });
      })
      .catch(error => {
        console.error('Error fetching Snowflake data:', error);
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setFilteredTableData(tableData);
    setOriginalTableData(tableData);
    setHasUnsavedChanges(false);
    setIsLoading(false);
  }, [tableData]);

  const filterData = (options) => {
    const hasSearchValue = options.searchValue && options.searchValue.trim() !== '';
    const comparisonKeys = {
      class1: 'parent_prdt_kind_nm',
      class2: 'prdt_kind_nm',
      item: 'item',
      sesn: 'sesn',
      designer: 'dsgnr_nm',
      sex: 'sex',
      fabInfo: 'fab_info',
    };

    const filteredResult = snowflakeData.filter((item) => {
      const searchValueMatch = hasSearchValue
        ? ['part_cd', 'color_cd', 'prdt_nm', 'prdt_nm_eng'].some((field) =>
            item[field]?.toString().toLowerCase().includes(options.searchValue.toLowerCase())
          )
        : true;
      const comparisonMatch = Object.keys(comparisonKeys).every((key) => {
        if (options[key].length === filterOptions[key].length) return true;
        return options[key].some(
          (option) => option.value.toUpperCase() === String(item[comparisonKeys[key]]).toUpperCase()
        );
      });
      return searchValueMatch && comparisonMatch;
    });

    setFilteredData(filteredResult);
    setCurrentFilterSesn(options.sesn || []);
  };

  useEffect(() => {
    if (lifeStyleList.length > 0) {
      setRowSelectionModel(lifeStyleList.map(item => item.lifeStyle));
    }
  }, [lifeStyleList]);

  useEffect(() => {
    if (filteredData.length > 0) {
      const lifeStyleCounts = filteredData.reduce((acc, data) => {
        const lifeStyle = data['lifestyle_nm'];
        acc[lifeStyle] = (acc[lifeStyle] || 0) + 1;
        return acc;
      }, {});
      const uniqueLifeStyles = Array.from(new Set(filteredData.map((data) => data['lifestyle_nm'])))
        .map((lifeStyle) => ({
          lifeStyle: `${lifeStyle} (${lifeStyleCounts[lifeStyle]})`,
        }));
      setLifeStyleList(uniqueLifeStyles);
    } else {
      setLifeStyleList([]);
    }
  }, [filteredData]);

  useEffect(() => {
    const lifeStyleOnly = rowSelectionModel.map((lifeStyle) =>
      lifeStyle.replace(/\s*\(\d+\)$/, '')
    );
    const newTableData = filteredData.filter((data) =>
      lifeStyleOnly.includes(data['lifestyle_nm'])
    );
    setTableData(newTableData);
  }, [rowSelectionModel, filteredData]);

  useEffect(() => {
    setTranslatedColumns(columns(language).map((column) => ({
      ...column,
      headerName: translateText({ text: column.headerName, globalLanguage: language }),
    })));
  }, [language]);

  const getExportColumns = useCallback(() => {
    return translatedColumns.filter(col => col.field !== 'img');
  }, [translatedColumns]);

  const applyFontStyle = (ws) => {
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[addr]) {
          ws[addr].s = { font: { name: '맑은 고딕', sz: 11 } };
        }
      }
    }
  };

  const downloadFormat = async () => {
    const response = await fetch('/api/cad-data/download-dx-format');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Style_Initial_Plan_Format.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const DATA_EXPORT_FIELDS = [
    'sesn', 'lifestyle_nm', 'part_cd', 'prdt_nm', 'prdt_nm_eng',
    'parent_prdt_kind_nm', 'prdt_kind_nm', 'item', 'fab_info', 'sex',
    'fit_info', 'length_info', 'tag_price', 'tag_price_cn', 'mfac_compy_nm',
    'dsgnr_nm', 'dsgnr_md_nm', 'dsgnr_mtrls_nm', 'dsgnr_goods_nm',
    'fab_type', 'size_range', 'color_cd',
  ];

  const downloadDataExcel = async () => {
    const rows = originalTableData.map(row => {
      const obj = {};
      DATA_EXPORT_FIELDS.forEach(field => {
        obj[field] = row[field] === null || row[field] === undefined ? '' : row[field];
      });
      return obj;
    });

    const response = await fetch('/api/cad-data/download-dx-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows }),
    });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Style_Initial_Plan_Data.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        // B4부터 읽도록 sheet ref를 B4 시작으로 조정 (col A 제외, row 4 = index 3)
        const sheetRef = XLSX.utils.decode_range(ws['!ref']);
        sheetRef.s.c = 1; // column B (index 1)
        sheetRef.s.r = 3; // row 4 (index 3)
        ws['!ref'] = XLSX.utils.encode_range(sheetRef);
        const rawJson = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const jsonData = rawJson
          .map(row => Object.fromEntries(Object.entries(row).filter(([k]) => k && k.trim() !== '')))
          .filter(row => Object.values(row).some(v => v !== ''));

        if (jsonData.length === 0) {
          alert('업로드한 파일에 데이터가 없습니다.');
          return;
        }

        const uploadedHeaders = Object.keys(jsonData[0]);
        const allHeaderMap = getAllHeaderToFieldMap();

        const unmapped = uploadedHeaders.filter(h => !allHeaderMap[h]);
        if (unmapped.length > 0) {
          alert(language === 'ko'
            ? `인식할 수 없는 헤더가 있습니다:\n${unmapped.join(', ')}`
            : `Unrecognized headers:\n${unmapped.join(', ')}`);
          return;
        }

        const mappedFields = new Set(uploadedHeaders.map(h => allHeaderMap[h]).filter(Boolean));
        const requiredFields = ['part_cd', 'color_cd'];
        const missingFields = requiredFields.filter(f => !mappedFields.has(f));
        if (missingFields.length > 0) {
          alert(language === 'ko'
            ? `필수 컬럼이 누락되었습니다:\n${missingFields.join(', ')}`
            : `Required columns are missing:\n${missingFields.join(', ')}`);
          return;
        }

        const tempData = jsonData.map((row, idx) => {
          const mapped = { _uploadId: idx };
          Object.entries(row).forEach(([header, value]) => {
            const field = allHeaderMap[header];
            if (field) mapped[field] = value;
          });
          return mapped;
        });

        const selectedSesns = currentFilterSesn.map(s => s.value.toUpperCase());
        const isAllSesnSelected = filterOptions && currentFilterSesn.length === filterOptions.sesn.length;

        if (!isAllSesnSelected && selectedSesns.length > 0) {
          const uploadSesns = [...new Set(tempData.map(row => String(row.sesn || '').toUpperCase()).filter(Boolean))];
          const mismatch = uploadSesns.filter(s => !selectedSesns.includes(s));
          if (mismatch.length > 0) {
            alert(language === 'ko'
              ? `시즌이 상이한 데이터가 있습니다. (${mismatch.join(', ')})\n필터 해제 후 업로드해주세요.`
              : `Season mismatch detected. (${mismatch.join(', ')})\nPlease clear the season filter before uploading.`);
            return;
          }
        }

        if (!isAllSesnSelected && selectedSesns.length === 1) {
          tempData.forEach(row => {
            if (!row.sesn || row.sesn === '') row.sesn = selectedSesns[0];
          });
        }

        setFilteredTableData(tempData);
        setHasUnsavedChanges(true);
      } catch (error) {
        console.error('파일 파싱 오류:', error);
        alert('파일을 읽는 중 오류가 발생했습니다.');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!filteredTableData || filteredTableData.length === 0) {
      alert('저장할 데이터가 없습니다.');
      return;
    }

    const makeKey = (row) => `${row.sesn}_${row.part_cd}_${row.color_cd}`;

    const originalKeys = new Set(originalTableData.map(makeKey));
    const currentKeys = new Set(filteredTableData.map(makeKey));

    const unchangedRows = snowflakeData.filter(row => {
      const key = makeKey(row);
      return !originalKeys.has(key) && !currentKeys.has(key);
    });

    const allRows = [...filteredTableData, ...unchangedRows];

    const compareFields = HEADER_FIELD_MAP
      .map(h => h.field)
      .filter(f => f !== 'color_cd' && f !== 'sesn' && f !== 'part_cd');

    const groups = {};
    filteredTableData.forEach(row => {
      const key = `${row.sesn || ''}_${row.part_cd || ''}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    const inconsistent = [];
    Object.entries(groups).forEach(([key, rows]) => {
      if (rows.length < 2) return;
      const base = rows[0];
      const diffFields = new Set();
      for (let i = 1; i < rows.length; i++) {
        compareFields.forEach(f => {
          if (String(rows[i][f] ?? '') !== String(base[f] ?? '')) {
            diffFields.add(f);
          }
        });
      }
      if (diffFields.size > 0) {
        const fieldLabels = [...diffFields].map(f => {
          const entry = HEADER_FIELD_MAP.find(h => h.field === f);
          return entry ? (language === 'ko' ? entry.ko : entry.en) : f;
        });
        inconsistent.push({ key, fields: fieldLabels });
      }
    });

    if (inconsistent.length > 0) {
      const detail = inconsistent.map(i => `  ${i.key}: ${i.fields.join(', ')}`).join('\n');
      alert(language === 'ko'
        ? `동일 시즌/품번 내 컬러 외 데이터가 일치하지 않습니다:\n${detail}`
        : `Data mismatch (excluding Color) within same Season/Style No:\n${detail}`);
      return;
    }

    const confirmMsg = language === 'ko'
      ? '새로운 정보로 저장하시겠습니까?'
      : 'Please reconfirm to save the data';
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/cad-data/save-upload-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows: allRows,
          createUser: accounts?.[0]?.name || 'SYSTEM'
        })
      });
      const result = await response.json();
      if (result.success) {
        alert(language === 'ko' ? '데이터 저장 완료' : 'Data saved successfully');
        setHasUnsavedChanges(false);
        loadData();
      } else {
        const errMsg = (language === 'ko' ? result.messageKo : result.messageEn) || result.message || '';
        alert(language === 'ko'
          ? '저장 실패: ' + errMsg
          : 'Save failed: ' + errMsg);
      }
    } catch (error) {
      console.error('저장 오류:', error);
      alert(language === 'ko'
        ? '저장 중 오류가 발생했습니다.'
        : 'An error occurred while saving.');
    } finally {
      setIsLoading(false);
    }
  };

  const processRowUpdate = useCallback((newRow, oldRow) => {
    const oldKey = oldRow._uploadId !== undefined ? oldRow._uploadId : `${oldRow['part_cd']}_${oldRow['color_cd']}`;
    setFilteredTableData((prev) =>
      prev.map((row) => {
        const rowKey = row._uploadId !== undefined ? row._uploadId : `${row['part_cd']}_${row['color_cd']}`;
        return rowKey === oldKey ? newRow : row;
      })
    );
    setHasUnsavedChanges(true);
    return newRow;
  }, []);

  const editableColumns = translatedColumns.map(col =>
    col.field === 'img' ? col : { ...col, editable: true }
  );

  return (
    <Page>
      {isLoading ? <BackdropPanel /> : null}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls"
        onChange={handleFileChange}
      />
      { hasPermission('cadDashboard-data', 'edit') ? (
        ismatrixBoardPage && onSubTabChange ? (
          <div style={{ position: 'fixed', top: '80px', left: '15px', zIndex: 1000, display: 'flex', gap: '4px' }}>
            <button style={{ fontSize: '12px', padding: '4px 8px', background: '#fff', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }} onClick={() => onSubTabChange('caddata')}>Style Initial Plan</button>
            <button style={{ fontSize: '12px', padding: '4px 8px', background: '#353339', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} disabled>Data Upload</button>
          </div>
        ) : (
          <Menu
            targetPaths={['/interstellar/X/caddata', '/interstellar/X/caddata/upload']}
            menuItems={[
              { path: '/interstellar/X/caddata', label: 'Style Initial Plan' },
              { path: '/interstellar/X/caddata/upload', label: 'Data Upload' }
            ]}
          />
        )
      ) : null}
      <LanguageButton position={{ top: ismatrixBoardPage ? '80px' : '10px', right: '10px', position: ismatrixBoardPage ? 'fixed' : 'absolute' }} />
      { !hasPermission('cadDashboard', 'view') ? <Unauthorized title="Style Initial Plan" contact="AX팀 국준호 과장" language={language} /> : 
      <Dashboard>
        <h1 style={{color:'#353339', margin: '30px'}}>Style Initial Plan</h1>
        {filterOptions ? (
          <CadDataFilter
            data={filterOptions}
            onSearch={filterData}
            isUploadPage
            downloadFormat={downloadFormat}
            downloadExcel={downloadDataExcel}
            onUpload={handleUpload}
            onSave={handleSave}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        ) : <></>}
        <Content
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            width: '100%'
          }}>
            <div style={{
              width: '220px',
              minWidth: '220px',
              height: '100%',
              borderRight: '1px solid #ddd',
              overflow: 'hidden',
            }}>
              <DataGridPremium
                sx={gridStyle}
                columns={[language === 'ko'
                  ? { field: 'lifeStyle', headerName: '라이프스타일', width: 200 }
                  : { field: 'lifeStyle', headerName: 'LifeStyle', width: 200 }
                ]}
                rows={lifeStyleList}
                getRowId={(row) => row['lifeStyle']}
                hideFooter
                checkboxSelection
                rowHeight={40}
                onRowSelectionModelChange={setRowSelectionModel}
                rowSelectionModel={rowSelectionModel}
              />
            </div>

            <div style={{
              flex: 1,
              height: '100%',
              overflow: 'hidden',
            }}>
              <DataGridPremium
                sx={gridStyle}
                columns={editableColumns}
                rows={filteredTableData}
                getRowId={(row) => row._uploadId !== undefined ? row._uploadId : `${row['part_cd']}_${row['color_cd']}`}
                hideFooter
                rowHeight={40}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(error) => console.error(error)}
              />
            </div>
          </div>
        </Content>
        <div style={{
          width: '100%',
          paddingLeft: '50px',
          margin: '-25px 0 5px 0',
          display: 'flex',
          justifyContent: 'flex-start',
          height: '20px'
        }}>
          <span style={{ fontSize: '0.8rem' }}>
            {translateText({text: '스타일 수 :', globalLanguage: language})} {new Set(filteredTableData.map(r => r.part_cd)).size}
            &nbsp;&nbsp;
            {translateText({text: 'SKU 수 :', globalLanguage: language})} {filteredTableData.length}
          </span>
        </div>
      </Dashboard>
      }
    </Page>
  );
}

export default CadDataDXUpload;

const HEADER_FIELD_MAP = [
  { field: 'sesn', ko: '시즌', en: 'Season' },
  { field: 'lifestyle_nm', ko: '라이프스타일', en: 'Life Style' },
  { field: 'part_cd', ko: '품번 ', en: 'Style No' },
  { field: 'prdt_nm', ko: '제품명', en: 'Description' },
  { field: 'prdt_nm_eng', ko: '제품명 영문', en: 'Description(Eng)' },
  { field: 'parent_prdt_kind_nm', ko: '대분류', en: 'Class1' },
  { field: 'prdt_kind_nm', ko: '중분류', en: 'Class2' },
  { field: 'item', ko: '아이템', en: 'Item' },
  { field: 'fab_info', ko: '소재명', en: 'Fabric Name' },
  { field: 'sex', ko: '성별', en: 'Gender' },
  { field: 'fit_info', ko: '핏', en: 'Fit' },
  { field: 'length_info', ko: '기장', en: 'Length' },
  { field: 'tag_price', ko: '한국택가', en: 'Tag Price(KR)' },
  { field: 'tag_price_cn', ko: '중국택가', en: 'Tag Price(CN)' },
  { field: 'mfac_compy_nm', ko: '협력사', en: 'Vendor' },
  { field: 'dsgnr_nm', ko: '디자이너', en: 'Designer' },
  { field: 'dsgnr_md_nm', ko: '기획', en: 'MD' },
  { field: 'dsgnr_mtrls_nm', ko: '소재', en: 'FSO MD' },
  { field: 'dsgnr_goods_nm', ko: '소싱', en: 'SO MD' },
  { field: 'fab_type', ko: '원단 타입', en: 'Fabric Type' },
  { field: 'size_range', ko: '운영 사이즈', en: 'Order Size' },
  { field: 'color_cd', ko: '컬러', en: 'Color' },
];

const FORMAT_XLSX_HEADER_MAP = {
  '제품명_국문': 'prdt_nm',
  '제품명_영문': 'prdt_nm_eng',
  '소재타입': 'fab_type',
  '운영사이즈': 'size_range',
};

const getAllHeaderToFieldMap = () => {
  const map = {};
  HEADER_FIELD_MAP.forEach(({ field, ko, en }) => {
    map[ko] = field;
    map[en] = field;
    map[ko.trim()] = field;
    map[en.trim()] = field;
  });
  Object.entries(FORMAT_XLSX_HEADER_MAP).forEach(([header, field]) => {
    map[header] = field;
    map[header.trim()] = field;
  });
  return map;
};

const columns = (language) => [
  // 기본 정보
  { field: 'sesn', headerName: '시즌', width: 100 },
  { field: 'lifestyle_nm', headerName: '라이프스타일', width: 100 },
  { field: 'part_cd', headerName: '품번 ', width: 100 },

  // 제품 정보
   {
    field: 'prdt_nm',
    headerName: '제품명',
    width: 300
  } ,
  {
    field: 'prdt_nm_eng',
    headerName: '제품명(영문)',
    width: 300
  },

  // 분류 정보
  { field: 'parent_prdt_kind_nm', headerName: '대분류', width: 90 },
  { field: 'prdt_kind_nm', headerName: '중분류', width: 100 },
  { field: 'item', headerName: '아이템', width: 90 },
  { field: 'fab_info', headerName: '소재명', width: 150 },
  { field: 'sex', headerName: '성별', width: 90 },

  // 스타일 정보
  { field: 'fit_info', headerName: '핏', width: 100 },
  { field: 'length_info', headerName: '기장', width: 100 },

  // 가격 정보
  { field: 'tag_price', headerName: '한국택가', width: 110 },
  { field: 'tag_price_cn', headerName: '중국택가', width: 110 },

  // 협력사 정보
  { field: 'mfac_compy_nm', headerName: '협력사', width: 200 },

  // 담당자 정보
  { field: 'dsgnr_nm', headerName: '디자이너', width: 100 },
  { field: 'dsgnr_md_nm', headerName: '기획', width: 100 },
  { field: 'dsgnr_mtrls_nm', headerName: '소재', width: 100 },
  { field: 'dsgnr_goods_nm', headerName: '소싱', width: 100 },

  { field: 'fab_type', headerName: '원단 타입', width: 100 },
  { field: 'size_range', headerName: '운영 사이즈', width: 100 },

  // 컬러 정보
  { field: 'color_cd', headerName: '컬러', width: 100 },
];

 
  const gridStyle = {
    width: '100%',
    fontSize: '0.8rem',
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#F9F9FB !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
    },
    '& .Mui-selected': {
      backgroundColor: 'rgba(0, 0, 0, 0.03) !important',
    },
    '& .MuiCheckbox-root': {
      color: '#666666',
      '&.Mui-checked': {
        color: '#666666',
      }
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer'
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none'
    }
  };

 
  const createOptions = (data, key, filterFn = (value) => value !== null) => {
    const options = Array.from(
      new Set(
        data.map((item) => (item[key] === null ? '-' : item[key])).filter(filterFn)
      )
    ).map((value) => ({
      value: key === 'lifestyle_nm' ? value.toUpperCase() : value,
      label: key === 'lifestyle_nm' ? value.toUpperCase() : value
    }));
  
    // 중복 제거 (distinct)
    const distinctOptions = Array.from(
      new Map(options.map((item) => [item.value, item])).values()
    );
  
    return distinctOptions.sort((a, b) => {
      // sesn : 숫자 내림차순, 영문 오름차순 정렬
      if (key === 'sesn') {
        const numA = parseInt(a.value, 10);
        const numB = parseInt(b.value, 10);
        const alphaA = a.value.slice(-2); // 'FW', 'SS' 등
        const alphaB = b.value.slice(-2);
  
        if (numA !== numB) {
          return numB - numA; // 숫자 내림차순
        }
        return alphaA.localeCompare(alphaB); // 영문 오름차순
      } else {
        // 기본 정렬 (예: '-'가 가장 먼저, 나머지는 알파벳 순)
        if (a.value === '-') return -1;
        if (b.value === '-') return 1;
        return a.value.localeCompare(b.value);
      }
    });
  };

